/**
 * 특수 벽돌 데이터 — 방어막(shield)/장갑(armor)/재생(regen). 엔티티성 데이터라 global.tsv(long)가 아닌
 * 전용 wide TSV로 외부화한다(수치 수정 후 `npm run export:data`).
 *
 *  - `data/specialBricks.tsv` → kind별 속성(색·보상배수·HP배율·상한 + kind 전용 필드). 미사용 컬럼은 0.
 *  - `data/specialBrickRules.tsv` → 월드 등장 비율 램프(worldMin 임계별 shield/armor/regen weight %).
 *
 * 소비처(brickAppearance·BattlefieldRunner)는 balance 배럴 경유로 SHIELD_BRICK/ARMOR_BRICK/REGEN_BRICK/
 * SPECIAL_RULES를 그대로 쓴다(이 모듈이 shape를 조립 — global 이관 전과 동일 인터페이스).
 */
import { BigNumber } from "@yjsgames/bignumber";
import bricksJson from "./generated/specialBricks.json";
import rulesJson from "./generated/specialBrickRules.json";
import { resolveShieldHits } from "../systems/markSpecialBricks.js";
import type { BrickCellKind } from "./ChapterData.js";

/** kind 행 조회(누락이면 데이터 오류로 throw — 부팅 시점 즉시 검출). */
function rowOf(kind: string): (typeof bricksJson)[number] {
  const row = bricksJson.find((b) => b.kind === kind);
  if (!row) throw new Error(`specialBricks.tsv: '${kind}' 행 누락`);
  return row;
}

const shield = rowOf("shield");
const armor = rowOf("armor");
const regen = rowOf("regen");

/**
 * 방어막 벽돌 — 타격 횟수 선소모(다단히트 빌드 유리). shieldHits는 챕터 스케일(초기 base → lateChapter 이상 late).
 */
export const SHIELD_BRICK = {
  tint: shield.tint,
  goldMultiplier: shield.goldMultiplier,
  hpFactor: shield.hpFactor,
  maxPerChapter: shield.maxPerChapter,
  shieldHits: {
    base: shield.shieldHitsBase,
    lateChapter: shield.shieldHitsLateChapter,
    late: shield.shieldHitsLate,
  },
} as const;

/** 장갑 벽돌 — 고정 방어력 감산(고화력 단발 유리). armor = 해당 챕터 baseHp × armorFraction(챕터 비례). */
export const ARMOR_BRICK = {
  tint: armor.tint,
  goldMultiplier: armor.goldMultiplier,
  hpFactor: armor.hpFactor,
  maxPerChapter: armor.maxPerChapter,
  armorFraction: armor.armorFraction,
} as const;

/** 재생 벽돌 — 주변 벽돌 주기 회복(우선 제거 타겟). */
export const REGEN_BRICK = {
  tint: regen.tint,
  goldMultiplier: regen.goldMultiplier,
  hpFactor: regen.hpFactor,
  maxPerChapter: regen.maxPerChapter,
  healIntervalMs: regen.healIntervalMs,
  healAmountRatio: regen.healAmountRatio,
  healRadius: regen.healRadius,
} as const;

/**
 * 특수 벽돌 월드별 등장 비율 규칙. 행을 worldMin 오름차순 정렬 후 컬럼을 배열로 모은다 —
 * resolveSpecialBrickWeights가 해당 월드의 셀별 확률(0~1)로 해석. W2 shield, W3 armor, W4 regen 순서로 해금.
 */
const sortedRules = [...rulesJson].sort((a, b) => a.worldMin - b.worldMin);
export const SPECIAL_RULES = {
  worldMin: sortedRules.map((r) => r.worldMin),
  shieldWeight: sortedRules.map((r) => r.shieldWeight),
  armorWeight: sortedRules.map((r) => r.armorWeight),
  regenWeight: sortedRules.map((r) => r.regenWeight),
} as const;

/** spawn 시 kind별로 주입할 특수 벽돌 BrickConfig 필드(해당 kind만 채움) — BrickConfig optional과 동형. */
export interface SpecialSpawnFields {
  readonly shieldHits?: number;
  readonly armor?: BigNumber;
  readonly healIntervalMs?: number;
  readonly healAmountRatio?: number;
  readonly healRadius?: number;
}

/**
 * 특수 벽돌 kind의 spawn 전용 필드를 조립(순수) — buildChapterGrid가 spawnBrick config에 spread. armor는
 * 해당 챕터 baseHp × armorFraction(챕터 비례), shieldHits는 챕터 스케일. 비-특수 kind는 빈 객체.
 */
export function specialBrickSpawnFields(
  kind: BrickCellKind,
  baseHp: BigNumber,
  chapter: number,
): SpecialSpawnFields {
  if (kind === "shield") return { shieldHits: resolveShieldHits(chapter, SHIELD_BRICK.shieldHits) };
  if (kind === "armor") return { armor: baseHp.mul(BigNumber.fromNumber(ARMOR_BRICK.armorFraction)) };
  if (kind === "regen") {
    return {
      healIntervalMs: REGEN_BRICK.healIntervalMs,
      healAmountRatio: REGEN_BRICK.healAmountRatio,
      healRadius: REGEN_BRICK.healRadius,
    };
  }
  return {};
}
